import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RichTextEditor } from "../components/RichText";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Tooltip,
  IconButton,
  MenuItem,
  CircularProgress,
  Checkbox,
  Chip,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import GroupIcon from "@mui/icons-material/Group";
import InventoryIcon from "@mui/icons-material/Inventory2";
import PlaceIcon from "@mui/icons-material/Place";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { ATTENDANCE_ROLES, LOCATION_TYPES, INCIDENT_TYPES, INCIDENT_DETAIL_TYPES, INCIDENT_STATUSES, RESPONSE_TEAM_DEPLOYMENTS, MOUNTAIN_SAFETY_DEPLOYMENTS, DISPOSAL_TRANSPORTS, DISPOSAL_DESTINATIONS, ADVISED_TO_RETIRE_OPTIONS, EVENT_LINK_LIST, MEDICAL_INCIDENT_TYPES, needsClinicalReview, sortByLocationType, locationSummaryLabel, locationSummaryOrder, OTHER_LOCATION } from "../constants";
import { incidentsPer1000Hours } from "../eventComparison";
import ClientContractManager from "../components/ClientContractManager";
import CollapsibleSection from "../components/CollapsibleSection";
import EngagementManager from "../components/EngagementManager";
import EventAllocationsDialog from "../components/EventAllocationsDialog";
import EventComparisonDialog from "../components/EventComparisonDialog";
import EventLocationsDialog from "../components/EventLocationsDialog";
import EventParticipantsDialog from "../components/EventParticipantsDialog";
import EventPersonnelDialog from "../components/EventPersonnelDialog";
import EventRaceManager from "../components/EventRaceManager";
import EventRouteManager from "../components/EventRouteManager";
import LinkManager from "../components/LinkManager";
import { API_BASE_URL } from "../config";
import { drugTotals } from "../incidentNotes";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";

const EVENTS_API = `${API_BASE_URL}/events`;
const ATT_API = `${API_BASE_URL}/attendances`;
const PARTICIPANTS_API = `${API_BASE_URL}/participants`;
const LOCATIONS_API = `${API_BASE_URL}/locations`;
const INCIDENTS_API = `${API_BASE_URL}/incidents`;

const BLANK = { name: "", abbreviation: "", start_date: "", end_date: "", information: "", total_running_hours: "",
                availability_open: false };

// The running-hours field is typed as text; blank is "not recorded", anything else
// must be a positive number (the backend refuses zero and below too).
const runningHoursInvalid = (value) => value !== "" && !(Number(value) > 0);

// The managed sides of an event, each in its own dialog, reachable either from a
// row in the list or from the event's own dialog. Each is gated by the view
// permission for what it manages, checked against that event.
//
// Allocations are the odd one out: they are gated on InventoryItems.view, which is
// a global role only (an allocation is part of an item's record — see
// routers/inventory_allocation.py), so unlike the others it never opens for
// someone whose access comes from being on the event.
const PANELS = [
  { kind: "personnel", label: "Personnel & teams", perm: "EventAttendances.view", icon: GroupIcon },
  { kind: "locations", label: "Locations", perm: "EventLocations.view", icon: PlaceIcon },
  { kind: "participants", label: "Participants", perm: "EventParticipants.view", icon: DirectionsRunIcon },
  { kind: "allocations", label: "Allocations", perm: "InventoryItems.view", icon: InventoryIcon },
];

// Count items by a derived key, returning [label, count] pairs. With an `order`
// list, labels found in it come first (in that order) and the rest follow
// alphabetically; otherwise the result is sorted by count (desc) then label.
//
// `include` seeds labels at zero so they are listed whether or not anything was
// counted against them. That is for a breakdown over a *fixed* option list where
// the absence is itself the answer — nobody was deployed — as against one keyed
// by the data's own vocabulary, where a label nothing recorded isn't a row.
const tally = (items, keyFn, { order, include, emptyLabel = "—" } = {}) => {
  const counts = new Map();
  (include || []).forEach(label => counts.set(label, 0));
  items.forEach(it => {
    const k = keyFn(it) || emptyLabel;
    counts.set(k, (counts.get(k) || 0) + 1);
  });
  const entries = [...counts.entries()];
  if (order) {
    const rank = (l) => { const i = order.indexOf(l); return i === -1 ? order.length : i; };
    entries.sort((a, b) => (rank(a[0]) - rank(b[0])) || a[0].localeCompare(b[0]));
  } else {
    entries.sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
  }
  return entries;
};

export default function Events() {
  const navigate = useNavigate();
  const { can } = useMe();
  const fullScreen = useFullScreenDialog();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true); // true until the first events fetch resolves
  const [form, setForm] = useState(BLANK);
  const [initialForm, setInitialForm] = useState(BLANK); // form snapshot at open, to detect unsaved changes
  const [editing, setEditing] = useState(null); // null | "new" | id
  // One-line summaries the Event dialog's collapsed sections show, reported by the
  // manager inside each (race tracking, routes, contracts, links). Cleared when a
  // different event opens, so the previous event's summary never shows.
  const [summaries, setSummaries] = useState({});
  useEffect(() => { setSummaries({}); }, [editing]);
  const summarise = (key) => (text) =>
    setSummaries(s => (s[key] === text ? s : { ...s, [key]: text }));
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDelete, setConfirmDelete] = useState(false); // delete-confirmation dialog
  // Save posts a new event, so a double-tap would file two (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();
  const [nameFilter, setNameFilter] = useState("");
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc | date_asc | name_asc | name_desc
  // The personnel / locations / participants dialog, overlaid on whatever is
  // behind it. `panelEvent` is kept when the panel closes so its title doesn't
  // blank out mid fade-out.
  const [panelKind, setPanelKind] = useState(null);
  const [panelEvent, setPanelEvent] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false); // multi-event comparison
  const [summaryOpen, setSummaryOpen] = useState(false); // event summary dialog
  const [summaryIncidents, setSummaryIncidents] = useState([]);
  const [summaryPeople, setSummaryPeople] = useState([]);
  const [summaryLocations, setSummaryLocations] = useState([]);
  const [summaryParticipants, setSummaryParticipants] = useState([]);

  const load = () => axios.get(EVENTS_API + "/").then(res => setEvents(res.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = events.filter(e =>
    (e.name || "").toLowerCase().includes(nameFilter.toLowerCase()) ||
    (e.abbreviation || "").toLowerCase().includes(nameFilter.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "date_asc":
        return (a.start_date || "").localeCompare(b.start_date || "");
      case "date_desc":
        return (b.start_date || "").localeCompare(a.start_date || "");
      case "name_asc":
        return (a.label_long || "").localeCompare(b.label_long || "");
      case "name_desc":
        return (b.label_long || "").localeCompare(a.label_long || "");
      default:
        return 0;
    }
  });

  const openPanel = (kind, event) => { setPanelEvent(event); setPanelKind(kind); };
  const closePanel = () => setPanelKind(null);

  const openNew = () => {
    setForm(BLANK); setInitialForm(BLANK);
    setEditing("new");
  };

  const openEdit = (e) => {
    const loaded = {
      name: e.name ?? "",
      abbreviation: e.abbreviation ?? "",
      start_date: e.start_date ?? "",
      end_date: e.end_date ?? "",
      information: e.information ?? "",
      total_running_hours: e.total_running_hours != null ? String(e.total_running_hours) : "",
      availability_open: !!e.availability_open,
    };
    setForm(loaded);
    setInitialForm(loaded);
    setEditing(e.id);
  };

  const close = () => {
    setEditing(null); setConfirmClose(false);
    setSummaryOpen(false);
    setSummaryIncidents([]); setSummaryPeople([]);
    setSummaryLocations([]); setSummaryParticipants([]);
  };

  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  // Only the event's own fields are on this dialog; everything reached from it
  // (personnel, locations, participants, links) saves instantly in its own dialog.
  const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialForm);
  const requestClose = () => (isDirty() ? setConfirmClose(true) : close());

  const toPayload = () => ({
    name: form.name,
    abbreviation: form.abbreviation || null,
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    information: form.information.trim() || null,
    total_running_hours: form.total_running_hours === "" ? null : Number(form.total_running_hours),
    availability_open: form.availability_open,
  });

  const save = () => runWrite(() => {
    const after = () => { load(); close(); };
    return editing === "new"
      ? axios.post(EVENTS_API + "/", toPayload()).then(after)
      : axios.put(`${EVENTS_API}/${editing}`, toPayload()).then(after);
  });

  const remove = () => {
    axios.delete(`${EVENTS_API}/${editing}`).then(() => {
      setConfirmDelete(false);
      load();
      close();
    });
  };

  const field = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
  });

  const dateRange = (e) => {
    if (!e.start_date) return "—";
    return e.end_date && e.end_date !== e.start_date ? `${e.start_date} → ${e.end_date}` : e.start_date;
  };

  // Event-scoped capabilities for the event currently open in the dialog.
  const evId = typeof editing === "number" ? editing : undefined;
  const currentEvent = events.find(e => e.id === editing);
  const canPanel = (perm, eventId) => can(perm, { eventId });

  // The summary counts each side of the event, so it loads them itself — they
  // live in their own dialogs now and aren't otherwise on hand. Each fetch is
  // skipped when the caller can't see that data, so its section reads as absent
  // rather than empty.
  const openSummary = () => {
    const params = { params: { event_id: editing } };
    axios.get(INCIDENTS_API + "/", params).then(res => setSummaryIncidents(res.data));
    if (canPanel("EventAttendances.view", evId)) {
      axios.get(ATT_API + "/", params).then(res => setSummaryPeople(res.data));
    } else { setSummaryPeople([]); }
    if (canPanel("EventLocations.view", evId)) {
      axios.get(LOCATIONS_API + "/", params).then(res => setSummaryLocations(res.data.sort(sortByLocationType)));
    } else { setSummaryLocations([]); }
    if (canPanel("EventParticipants.view", evId)) {
      axios.get(PARTICIPANTS_API + "/", params).then(res => setSummaryParticipants(res.data));
    } else { setSummaryParticipants([]); }
    setSummaryOpen(true);
  };

  // Which pre-defined location an incident was at. There's no FK — picking one on
  // an incident copies its type into location_type and its name into
  // location_details (both still editable), so match on that pair; anything else
  // (a typed-in or edited location) counts as Other.
  const incidentLocationKey = (i) => {
    const match = summaryLocations.find(l =>
      (l.type || "") === (i.location_type || "") &&
      (l.name || "") === (i.location_details || ""));
    return match ? locationSummaryLabel(match) : OTHER_LOCATION;
  };

  // A labelled "Label: count" breakdown list, for the summary dialog.
  const countList = (rows) => (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
      {rows.length === 0 ? (
        <Typography component="li" variant="body2" color="text.secondary">None</Typography>
      ) : rows.map(([label, count]) => (
        <Typography component="li" variant="body2" key={label}>
          {label}: <strong>{count}</strong>
        </Typography>
      ))}
    </Box>
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Events</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {/* Several events' summaries side by side. Super Admin only — see
              Events.compareSummaries in core/permissions.py. */}
          {can("Events.compareSummaries") && (
            <Button startIcon={<CompareArrowsIcon />} onClick={() => setCompareOpen(true)}>
              Compare events
            </Button>
          )}
          {can("Events.add") && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add event</Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Filter by name" size="small" value={nameFilter}
                   onChange={e => setNameFilter(e.target.value)} />
        <TextField label="Sort by" select size="small" sx={{ minWidth: 200 }} value={sortBy}
                   onChange={e => setSortBy(e.target.value)}>
          <MenuItem value="date_desc">Date (newest first)</MenuItem>
          <MenuItem value="date_asc">Date (oldest first)</MenuItem>
          <MenuItem value="name_asc">Name (A–Z)</MenuItem>
          <MenuItem value="name_desc">Name (Z–A)</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {filtered.length} of {events.length}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Event</TableCell>
                <TableCell>Abbreviation</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell align="right">Manage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(e => (
                <TableRow hover key={e.id} onClick={() => openEdit(e)} sx={{ cursor: "pointer" }}>
                  <TableCell>
                    {e.label_long}
                    {e.availability_open && (
                      <Chip label="Taking availability" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{e.label_short}</TableCell>
                  <TableCell>{dateRange(e)}</TableCell>
                  {/* Straight to one side of the event, without opening it first.
                      stopPropagation so the row's own click doesn't also fire. */}
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {PANELS.filter(p => canPanel(p.perm, e.id)).map(({ kind, label, icon: Icon }) => (
                      <Tooltip key={kind} title={label}>
                        <IconButton size="small" aria-label={`${label} — ${e.label_long}`}
                                    onClick={ev => { ev.stopPropagation(); openPanel(kind, e); }}>
                          <Icon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? <CircularProgress size={28} />
          : events.length === 0 ? "No events yet — add one." : "No events match your filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editing !== null} onClose={requestClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>
          {editing === "new"
            ? "Add event"
            : `${form.name}${form.start_date ? ` ${form.start_date.slice(0, 4)}` : ""}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth required {...field("name")} />
            <TextField label="Abbreviation" fullWidth {...field("abbreviation")} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Start date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                         {...field("start_date")} />
              <TextField label="End date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                         {...field("end_date")} />
            </Box>
            <TextField label="Total running hours" type="number" fullWidth
                       slotProps={{ htmlInput: { min: 0, step: "any" } }}
                       error={runningHoursInvalid(form.total_running_hours)}
                       helperText={runningHoursInvalid(form.total_running_hours)
                         ? "Must be a number above zero, or left blank."
                         : "Every participant's time on the course added together. The summary uses it for incidents per 1,000 running hours."}
                       {...field("total_running_hours")} />
            {/* Rich text (the incident editor's Markdown, without photos). Plain text
                written before it was rich still reads the same: line breaks are kept. */}
            <Box>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Event information
              </Typography>
              <RichTextEditor value={form.information} minRows={4} photos={false}
                              onChange={v => setForm(f => ({ ...f, information: v }))} />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, mx: 1.75 }}>
                Shown to everyone on the event (meeting times, briefings, …), and to the team
                on My events while it's taking availability.
              </Typography>
            </Box>
            {/* The team put themselves forward from My events while this is ticked
                and the event is still to come (core/event_availability.py). */}
            <Box>
              <FormControlLabel
                control={<Checkbox checked={form.availability_open}
                                   onChange={e => setForm({ ...form, availability_open: e.target.checked })} />}
                label="Open for availability" />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4, mt: -0.5 }}>
                Everyone can see this event on My events and say they're available, with notes.
                They show up here as Available.
              </Typography>
            </Box>

            {/* Save/Cancel apply to the event fields above — nothing below them. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              {editing !== "new" && can("Events.delete") && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )}
              {editing !== "new" && (
                <Button startIcon={<SummarizeIcon />} onClick={openSummary}>
                  View summary
                </Button>
              )}
              {editing !== "new" && (
                <Button startIcon={<ReportProblemIcon />}
                        onClick={() => navigate(`/incidents?event=${editing}`)}>
                  View incidents
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={requestClose}>Cancel</Button>
              {(editing === "new" ? can("Events.add") : can("Events.edit")) && (
                <Button variant="contained"
                        disabled={!form.name.trim() || runningHoursInvalid(form.total_running_hours) || saving}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Box>

            {editing !== "new" && (
              <>
                {/* Each of these opens over this dialog and saves as you go, so
                    there is nothing here to Save or Cancel. */}
                <Divider textAlign="left">Manage</Divider>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {PANELS.filter(p => canPanel(p.perm, evId)).map(({ kind, label, icon: Icon }) => (
                    <Button key={kind} size="small" variant="outlined" startIcon={<Icon />}
                            onClick={() => openPanel(kind, currentEvent)}>
                      {label}
                    </Button>
                  ))}
                </Box>

                {/* The sections below start collapsed to a one-line summary each
                    manager reports, and open to edit; each saves as you go. */}

                {/* Races and their live-tracking addresses. Small enough to belong
                    here rather than in a dialog of its own; an incident uses it to
                    offer a link to the patient's tracking page. */}
                <CollapsibleSection title="Race tracking" summary={summaries.races}>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    One tracking link per race — an incident adds the patient's race
                    number to the end of it.
                  </Typography>
                  <EventRaceManager eventId={editing} canEdit={can("Events.edit")}
                                    onSummary={summarise("races")} />
                </CollapsibleSection>

                {/* Course routes from GPX files, each with its start and course
                    close; drawn on the incidents map. */}
                <CollapsibleSection title="Routes" summary={summaries.routes}>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    One GPX per course — per distance, or per day of a stage race — with the times
                    it's open.
                  </Typography>
                  <EventRouteManager eventId={editing} canEdit={can("Events.edit")}
                                     onSummary={summarise("routes")} />
                </CollapsibleSection>

                {/* The agreement with the organiser: chosen, uploaded, signed for
                    Shelter Stone and sent for them to sign by link. */}
                {can("Contracts.client") && (
                  <CollapsibleSection title="Organiser contract" summary={summaries.client}>
                    <ClientContractManager eventId={editing} onSummary={summarise("client")} />
                  </CollapsibleSection>
                )}

                {/* What personnel engagement schedules are merged from: the
                    template, the location and the Role fields. Sending them is
                    done per person (attendance) or in bulk from Personnel & teams. */}
                {can("Contracts.personnel") && (
                  <CollapsibleSection title="Engagement schedules" summary={summaries.engagement}>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                      Personnel contracts are merged from this. Send them from Personnel &amp; teams.
                    </Typography>
                    <EngagementManager eventId={editing} onSummary={summarise("engagement")} />
                  </CollapsibleSection>
                )}

                {/* Information links (documents / external links) */}
                <CollapsibleSection title={EVENT_LINK_LIST.heading} summary={summaries.links}>
                  <LinkManager kind={EVENT_LINK_LIST.kind} eventId={editing} dense
                               empty={EVENT_LINK_LIST.empty} addLabel={EVENT_LINK_LIST.addLabel}
                               canAdd={can("Events.edit")} canEdit={can("Events.edit")}
                               canDelete={can("Events.edit")} onSummary={summarise("links")} />
                </CollapsibleSection>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* The managed sides of an event, each overlaying whatever opened it */}
      <EventPersonnelDialog event={panelEvent} open={panelKind === "personnel"} onClose={closePanel} />
      <EventLocationsDialog event={panelEvent} open={panelKind === "locations"} onClose={closePanel} />
      <EventParticipantsDialog event={panelEvent} open={panelKind === "participants"} onClose={closePanel} />
      <EventAllocationsDialog event={panelEvent} open={panelKind === "allocations"} onClose={closePanel} />

      {/* Several events compared. It takes the page's own event list — the tree
          is built from the names and years already on screen. */}
      <EventComparisonDialog open={compareOpen} onClose={() => setCompareOpen(false)}
                             events={events} />

      {/* Event summary */}
      <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>
          Summary — {form.name}{form.start_date ? ` ${form.start_date.slice(0, 4)}` : ""}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{dateRange(form)}</Typography>

            {canPanel("EventAttendances.view", evId) && (
              <>
                <Divider textAlign="left">Personnel ({summaryPeople.length})</Divider>
                {countList(tally(summaryPeople, a => a.role, { order: ATTENDANCE_ROLES, emptyLabel: "No event role" }))}
              </>
            )}

            {canPanel("EventLocations.view", evId) && (
              <>
                <Divider textAlign="left">Locations ({summaryLocations.length})</Divider>
                {countList(tally(summaryLocations, l => l.type, { order: LOCATION_TYPES, emptyLabel: "No type" }))}
              </>
            )}

            {canPanel("EventParticipants.view", evId) && (
              <>
                <Divider textAlign="left">Participants ({summaryParticipants.length})</Divider>
                {countList(tally(summaryParticipants, p => p.race, { emptyLabel: "No race" }))}
              </>
            )}

            <Divider textAlign="left">Incidents ({summaryIncidents.length})</Divider>
            {/* The rate is over the incidents counted above, by the same rule as
                the comparison report (incidentsPer1000Hours). Read off the form,
                like the title, so it follows hours just typed in. */}
            {runningHoursInvalid(form.total_running_hours) || form.total_running_hours === "" ? (
              <Typography variant="body2" color="text.secondary">
                Running hours not recorded — add them to the event for a rate per 1,000 hours.
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                <Typography component="li" variant="body2">
                  Running hours: <strong>{Number(form.total_running_hours)}</strong>
                </Typography>
                <Typography component="li" variant="body2">
                  All incidents per 1,000 running hours: <strong>
                    {incidentsPer1000Hours(summaryIncidents.length, form.total_running_hours)}
                  </strong>
                </Typography>
                <Typography component="li" variant="body2">
                  Medical incidents (Major + Minor) per 1,000 running hours: <strong>
                    {incidentsPer1000Hours(
                      summaryIncidents.filter(i => MEDICAL_INCIDENT_TYPES.includes(i.incident_type)).length,
                      form.total_running_hours)}
                  </strong>
                </Typography>
              </Box>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By type</Typography>
            {countList(tally(summaryIncidents, i => i.incident_type, { order: INCIDENT_TYPES, emptyLabel: "No type" }))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By detail type / subtype</Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
              {summaryIncidents.length === 0 ? (
                <Typography component="li" variant="body2" color="text.secondary">None</Typography>
              ) : tally(summaryIncidents, i => i.detail_type,
                        { order: INCIDENT_DETAIL_TYPES, emptyLabel: "No detail type" }).map(([type, count]) => (
                <Box component="li" key={type}>
                  <Typography variant="body2">{type}: <strong>{count}</strong></Typography>
                  {countList(tally(
                    summaryIncidents.filter(i => (i.detail_type || "No detail type") === type),
                    i => i.detail_subtype, { emptyLabel: "No subtype" }))}
                </Box>
              ))}
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By location</Typography>
            {countList(tally(summaryIncidents, incidentLocationKey,
                             { order: locationSummaryOrder(summaryLocations) }))}
            {/* Every deployment type is listed, including the ones nothing was
                counted against: "no Response Team called out all weekend" is a
                finding, and a breakdown that simply omits the type can't say it.
                A blank is still not a category, so incidents recording no
                deployment are filtered out rather than bucketed. */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By Response Team deployment</Typography>
            {countList(tally(summaryIncidents.filter(i => i.response_team_deployment),
                             i => i.response_team_deployment,
                             { order: RESPONSE_TEAM_DEPLOYMENTS,
                               include: RESPONSE_TEAM_DEPLOYMENTS }))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By Mountain Safety deployment</Typography>
            {countList(tally(summaryIncidents.filter(i => i.mountain_safety_deployment),
                             i => i.mountain_safety_deployment,
                             { order: MOUNTAIN_SAFETY_DEPLOYMENTS,
                               include: MOUNTAIN_SAFETY_DEPLOYMENTS }))}
            {/* Every option listed, zero included, like the deployments: "nobody
                left by helicopter" is a finding too. */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By disposal transport</Typography>
            {countList(tally(summaryIncidents.filter(i => i.disposal_transport),
                             i => i.disposal_transport,
                             { order: DISPOSAL_TRANSPORTS, include: DISPOSAL_TRANSPORTS }))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By disposal destination</Typography>
            {countList(tally(summaryIncidents.filter(i => i.disposal_destination),
                             i => i.disposal_destination,
                             { order: DISPOSAL_DESTINATIONS, include: DISPOSAL_DESTINATIONS }))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>By status</Typography>
            {countList(tally(summaryIncidents, i => i.status,
                             { order: INCIDENT_STATUSES, emptyLabel: "No status" }))}

            {/* The clinical figures, together and behind the clinical gate. A reader
                without it (the Race Organiser) is served these fields blanked and the
                drug notes dropped altogether (core/clinical.py), so every count here
                would come out as zero — which reads as fact rather than as "not shown
                to you". The section is absent for them instead, as Personnel and
                Participants are for someone who can't see those. */}
            {canPanel("Incidents.viewClinical", evId) && (
              <>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Advised to retire: <strong>{summaryIncidents.filter(i => i.advised_to_retire).length}</strong>
                </Typography>
                {/* What they did about it — only when there is something to break
                    down, or it would read "None" under a zero. */}
                {summaryIncidents.some(i => i.advised_to_retire)
                  && countList(tally(summaryIncidents.filter(i => i.advised_to_retire),
                                     i => i.advised_to_retire, { order: ADVISED_TO_RETIRE_OPTIONS }))}
                {/* Summed off the incidents' notes, because the note *is* the
                    administration record — there is no drug-administration table
                    (core/incident_drugs.py). Totals are amounts given, so two packs
                    on one incident and one on another read as 3. */}
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Drugs given</Typography>
                {countList(drugTotals(summaryIncidents))}
                {/* The same test as the Incidents page's "Pending clinical review"
                    filter (needsClinicalReview in constants.js), so the count and the
                    list it sends you to agree: a Management Incident with no clinical
                    content was never awaiting review. */}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Pending clinical review: <strong>
                    {summaryIncidents.filter(i => !i.clinical_review_at && needsClinicalReview(i)).length}
                  </strong>
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete event?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {form.name} and remove all personnel associations with it. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={remove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this event. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={close}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
